import { useQueryClient, InfiniteData, UseMutationOptions } from 'react-query';
import produce from 'immer';

import { useNotify } from 'hooks';
import {
  GetClientDepartmentsByStatusQuery,
  GetClientLocationsByStatusQuery,
  GetUserSearchesV2Query,
  GetUserSearchesV2QueryVariables,
  UpdateUserManagementUserProfileMutation,
  UpdateUserManagementUserProfileMutationVariables,
  useGetClientDepartmentsByStatusQuery,
  useGetClientLocationsByStatusQuery,
  useGetUserSearchesV2Query,
} from 'api';

type Mutation = UpdateUserManagementUserProfileMutation;
type MutationVariables = UpdateUserManagementUserProfileMutationVariables;
type QueryData = InfiniteData<GetUserSearchesV2Query>;
type QueryVariables = GetUserSearchesV2QueryVariables;
type DepartmentsQuery = GetClientDepartmentsByStatusQuery;
type LocationsQuery = GetClientLocationsByStatusQuery;

const useUpdateUserManagementUserProfile = (variables: QueryVariables) => {
  const queryClient = useQueryClient();
  const queryKey = useGetUserSearchesV2Query.getKey(variables);
  const notify = useNotify();

  const departmentQueryKey = useGetClientDepartmentsByStatusQuery.getKey({});
  const locationQueryKey = useGetClientLocationsByStatusQuery.getKey({});

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async ({ input }) => {
      await queryClient.cancelQueries(queryKey);

      const departmentState = queryClient.getQueryData<DepartmentsQuery>(departmentQueryKey);
      const locationState = queryClient.getQueryData<LocationsQuery>(locationQueryKey);
      const departments = departmentState?.getClientDepartmentsByStatus.client_departments;
      const locations = locationState?.getClientLocationsByStatus.client_locations;

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.pages.forEach(page => {
            const { getUserSearchesV2 } = page;
            const { results } = getUserSearchesV2;

            const index = results.findIndex(result => result.id === input.id);
            const prevResult = results[index];

            const userProfile = { ...prevResult, ...input };

            if (input.department_id) {
              userProfile.department_title =
                departments.find(dep => dep.id === input.department_id)?.title ?? '';
            }

            if (input.location_id) {
              userProfile.location_title =
                locations.find(loc => loc.id === input.location_id)?.title ?? '';
            }

            getUserSearchesV2.results[index] = userProfile;
          });
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
  };

  return mutationOptions;
};

export default useUpdateUserManagementUserProfile;
